import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, GenerateScriptPanel, Text } from '@/components';
import { useState, useEffect } from 'react';
import { useConfigContext } from './hoc';
import { GenerateScriptPanelProvider } from './hoc/context/generate-script-panel-context';
import { useSetApiConfig } from './hoc/hooks/use-set-api-config';
import { useAtsEmbed } from './hoc/hooks/use-ats-embed';
import { useEmbedNavigation } from './hoc/hooks/use-embed-navigation';
import { isAtsEmbedActive } from './hoc/api/ats-embed';
import { refreshAccessToken, vritiApiClient } from './hoc/api/api-service';
import { installExpiredSessionRedirect } from './utils/auth-session-guard';
import { consumeSharedSessionAdoption, migrateLegacyTokens, watchForLegacyDomainCookies, writeUserDetails, } from './utils/auth-token-store';
import { installSilentRefresh } from './utils/auth-refresh';
import { cn } from './utils/class-utils';
import useAuthStatus from './hooks/user-auth-status';
import { AiInterviewLoginWidget } from './login-widget';
import ConverseChatWidget from './converse-chat-widget';
import { defaultConfig, defaultTheme } from './constants';
import { canAccessAdminConsole, getUserProfileData, logoutUser, truncatedName } from './utils';
import { Popover, Tooltip } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import UserAvatar from './components/user-avatar/user-avatar';
import useDashTheme from './components/dashboard/useDashTheme';
import { FullScreenLoader } from './components/ui/full-screen-loader';
import { ConnectionStatusBar, isRecruiterAppPath } from './components/connection-status-bar';
import { requestTourStart } from './lib/product-tour';
function App({ isPreview = false, children }) {
    var _a, _b, _c, _d, _e, _f, _g;
    const [isManageView, setIsManageView] = useState(true);
    // Gate protected content until the access token is resolved (refresh-first),
    // so the refresh-token call finishes before any other API fires.
    const [authReady, setAuthReady] = useState(false);
    const location = useLocation();
    const path = location.pathname;
    const { config: { shouldIncludeAdminModule, accessed_from }, } = useConfigContext();
    useSetApiConfig();
    // ATS embed: install the postMessage token receiver. `waitingForToken` is true
    // only for an embedded ATS iframe that has no token yet — false for every
    // standalone / Vriti flow, so nothing changes there.
    const { waitingForToken } = useAtsEmbed();
    // Embedded only: keep "open in a new tab" links inside the iframe, so a job
    // card doesn't drop the recruiter out of the host ATS.
    useEmbedNavigation();
    const navigate = useNavigate();
    const [dashTheme] = useDashTheme();
    // VA-27781: install the expired-session redirect BEFORE anything issues a
    // request, so a token that dies mid-session ends at the login screen instead of
    // leaving the user on a page whose every call quietly 401s.
    useEffect(() => {
        // VA-27781: move any pre-cookie localStorage token into a cookie that expires
        // with it, so people already signed in are not logged out by this change and
        // do not keep reading a token that never expires. Runs before the redirect
        // guard so the first read already sees the migrated value.
        // Still called explicitly, though the token store now also migrates on
        // import — this stays as the belt-and-braces path for any entry point that
        // reaches App before touching the store.
        migrateLegacyTokens();
        installExpiredSessionRedirect();
        // Silent renewal is installed in the bootstrap (src/index.dev.tsx), NOT
        // here: <App> is a descendant of <PrivateRoute>, so a guard holding the
        // route for a renewal would prevent this from ever running. Re-armed here
        // only for host apps that mount <App> directly, bypassing the router.
        // Keep the session alive by renewing the access token shortly BEFORE it
        // expires, so a signed-in user is never bounced to login mid-task and never
        // has to reload the page. Must run after migration so it reads the cookie.
        installSilentRefresh(refreshAccessToken);
        // VA-28199: IAM mints its per-environment cookies on Domain=.vriti.ai at
        // any point after this tab is already open (a login redirect or SSO
        // refresh in another tab), not only before it. The module-load sweep in
        // auth-token-store only catches what already existed when the module was
        // first imported, so keep sweeping on focus/visibility and a slow
        // interval for as long as the app is mounted.
        return watchForLegacyDomainCookies();
    }, []);
    useEffect(() => {
        const w = window;
        // Skip the subscription interceptor in an ATS embed: it gates on the
        // standalone app's identity/permissions and blanks the embedded view.
        // Backend consumption tracking is unaffected (it runs on the API side).
        if (!isAtsEmbedActive() && typeof w.installSubscriptionInterceptor === 'function') {
            w.installSubscriptionInterceptor();
        }
    }, []);
    // A child service is embedded by a host that owns the page: Vriti sets
    // `accessed_from`, an ATS mounts us in an iframe. The host then owns the
    // token (see below) and any chat assistant on the page.
    const isChildService = !!accessed_from || (typeof window !== 'undefined' && window.self !== window.top);
    useEffect(() => {
        let active = true;
        const release = () => {
            if (active)
                setAuthReady(true);
        };
        // Only a standalone ("self") app mints its own token. When running as a
        // child service the parent owns the token, so skip the refresh and
        // release immediately.
        if (isChildService) {
            // ATS embed: hold until the parent posts the recruiter token
            // (waitingForToken flips false on receipt or via the failsafe timeout).
            // For every other child-service flow waitingForToken is already false.
            if (!waitingForToken)
                release();
            return () => {
                active = false;
            };
        }
        // Self app: mint a fresh ai-interview-scoped access token from the stored
        // refresh token and overwrite the `access` value FIRST, then release the
        // protected content so other APIs use the fresh token. Non-destructive
        // (no-op when not logged in / on failure); 5s failsafe so it never hangs.
        const timer = setTimeout(release, 5000);
        refreshAccessToken().finally(() => {
            clearTimeout(timer);
            release();
        });
        // Signed in from the recruiter app's shared session (auth-token-store
        // `adoptSharedSession`): load the profile the login screen would have saved.
        if (consumeSharedSessionAdoption()) {
            vritiApiClient
                .get('get-organization-profile/')
                .then((data) => {
                if (data === null || data === void 0 ? void 0 : data.data) {
                    writeUserDetails(JSON.stringify(data.data));
                    window.dispatchEvent(new Event('authChange'));
                }
            })
                .catch(() => { });
        }
        return () => {
            active = false;
            clearTimeout(timer);
        };
    }, [accessed_from, waitingForToken]);
    const isLoggedIn = useAuthStatus();
    const userData = getUserProfileData();
    const isInterviewDetailPage = /^\/interview\/interview-[a-zA-Z0-9]+$/.test(path);
    // '/' is the dashboard only when logged in — logged-out it's the full-page,
    // window-scrolling landing page, which must not get the dashboard shell's
    // fixed-viewport-height + overflow-hidden treatment below.
    // '/' is the landing for everyone except a signed-in ATS embed, which frames
    // '/?ats_connector=…' and keeps the dashboard there.
    const embedHome = path === '/' && isLoggedIn && isAtsEmbedActive();
    const isDashboardPage = path === '/dashboard' || embedHome;
    // The marketing pages: '/' (the landing, except a signed-in ATS embed), the
    // '/ai-interview' redirect to it, the
    // '/overview' product movie. They scroll the window and render their own nav,
    // so they get no app header and no overflow container (either would break
    // their pinned scenes).
    const isLandingPage = (path === '/' && !embedHome) ||
        path === '/ai-interview' ||
        path === '/ai-interview/about' ||
        path === '/ai-interview/contact' ||
        path === '/careers' ||
        path === '/career-guide' ||
        path === '/overview' ||
        path === '/privacy-policy' ||
        path === '/terms-of-use' ||
        path === '/blog' ||
        path.startsWith('/blog/');
    // Pages that render the dashboard Sidebar/TopBar chrome themselves
    // (DashboardShell) — no separate app header for them.
    // `/admin/*` is the standalone admin console: it renders its OWN chrome
    // (sidebar + header + logout), so like the dashboard shell it must suppress
    // the shared recruiter header and own a fixed full-viewport height.
    const usesDashShell = isDashboardPage ||
        path.startsWith('/job-description') ||
        path.startsWith('/interview-report') ||
        path.startsWith('/schedule-interview') ||
        path.startsWith('/my-settings') ||
        path.startsWith('/organization-profile') ||
        path.startsWith('/admin');
    const [anchorEl, setAnchorEl] = useState(null);
    const fullName = ((_a = userData === null || userData === void 0 ? void 0 : userData.representative_details) === null || _a === void 0 ? void 0 : _a.full_name) || 'User';
    const open = Boolean(anchorEl);
    const handleAvatarClick = (event) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);
    const handleMyProfile = () => {
        var _a;
        handleClose();
        const representativeId = (_a = userData === null || userData === void 0 ? void 0 : userData.representative_details) === null || _a === void 0 ? void 0 : _a.representative_id;
        navigate(`/representative-profile${representativeId ? `?id=${representativeId}` : ''}`);
    };
    const handleOrgProfile = () => {
        handleClose();
        navigate('/organization-profile');
    };
    // VA-27514 — Account Settings must be reachable from every page's profile
    // menu, not just /dashboard. Same target as the dashboard's own menu item.
    const handleAccountSettings = () => {
        handleClose();
        navigate('/my-settings');
    };
    // VA-29262: the tour runs on the dashboard overview.
    const handleProductTour = () => {
        handleClose();
        requestTourStart();
        navigate('/dashboard');
    };
    const handleAdminConsole = () => {
        handleClose();
        navigate('/admin/console');
    };
    const handleLogout = () => {
        var _a, _b;
        handleClose();
        logoutUser();
        if (window.Auth) {
            window.dispatchEvent(new Event('authChange'));
            (_b = (_a = window.Auth).logout) === null || _b === void 0 ? void 0 : _b.call(_a);
        }
        window.location.href = '/';
    };
    if (!isLoggedIn && shouldIncludeAdminModule) {
        _jsx(AiInterviewLoginWidget, { config: defaultConfig, theme: defaultTheme });
    }
    const profileMenu = (_jsxs(Popover, { open: open, anchorEl: anchorEl, onClose: handleClose, anchorOrigin: { vertical: 'bottom', horizontal: 'right' }, transformOrigin: { vertical: 'top', horizontal: 'right' }, PaperProps: {
            sx: {
                mt: 1,
                minWidth: 200,
                borderRadius: '8px',
                boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                overflow: 'hidden',
            },
        }, children: [_jsx(Tooltip, { title: fullName, arrow: true, placement: "left", children: _jsxs("div", { className: "px-4 py-3 border-b border-gray-100 flex items-center gap-3", children: [((_b = userData === null || userData === void 0 ? void 0 : userData.representative_details) === null || _b === void 0 ? void 0 : _b.representative_image_link) ? (_jsx("img", { src: userData.representative_details.representative_image_link, alt: fullName, className: "w-9 h-9 rounded-full shrink-0 object-cover" })) : (_jsx("div", { className: "w-9 h-9 rounded-full flex items-center justify-center shrink-0 text-sm font-bold text-white", style: { background: 'linear-gradient(135deg, #324ECF, #9810FA)' }, children: fullName.charAt(0).toUpperCase() })), _jsxs("div", { className: "min-w-0", children: [_jsx("p", { className: "text-sm font-semibold text-gray-800 truncate", children: truncatedName(userData) }), _jsx("p", { className: "text-xs text-gray-400 truncate", children: ((_c = userData === null || userData === void 0 ? void 0 : userData.representative_details) === null || _c === void 0 ? void 0 : _c.current_designation) || 'Member' })] })] }) }), _jsxs("div", { className: "py-1", children: [_jsx("button", { onClick: handleMyProfile, className: `w-full text-left px-4 py-2 text-sm font-medium transition-colors cursor-pointer ${path.startsWith('/representative-profile')
                            ? 'bg-[#EEF1FD] text-[#324ECF]'
                            : 'text-gray-700 hover:bg-gray-50'}`, children: "My Profile" }), _jsx("button", { onClick: handleOrgProfile, className: `w-full text-left px-4 py-2 text-sm font-medium transition-colors cursor-pointer ${path.startsWith('/organization-profile')
                            ? 'bg-[#EEF1FD] text-[#324ECF]'
                            : 'text-gray-700 hover:bg-gray-50'}`, children: "Organization Profile" }), _jsx("button", { onClick: handleAccountSettings, className: `w-full text-left px-4 py-2 text-sm font-medium transition-colors cursor-pointer ${path.startsWith('/my-settings') || path.startsWith('/account-settings')
                            ? 'bg-[#EEF1FD] text-[#324ECF]'
                            : 'text-gray-700 hover:bg-gray-50'}`, children: "Account Settings" }), _jsx("button", { onClick: handleProductTour, className: 'w-full text-left px-4 py-2 text-sm font-medium transition-colors cursor-pointer text-gray-700 hover:bg-gray-50', children: "Take a product tour" }), canAccessAdminConsole() && (_jsx("button", { onClick: handleAdminConsole, className: `w-full text-left px-4 py-2 text-sm font-medium transition-colors cursor-pointer ${path.startsWith('/admin/console')
                            ? 'bg-[#EEF1FD] text-[#324ECF]'
                            : 'text-gray-700 hover:bg-gray-50'}`, children: "Admin Console" })), _jsx("div", { className: "border-t border-gray-100 mt-1 pt-1", children: _jsx("button", { onClick: handleLogout, className: "w-full text-left px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer", children: "Logout" }) })] })] }));
    const avatarButton = (_jsx("div", { className: "cursor-pointer border-[3px] border-[#324ECF] w-[48px] h-[48px] flex justify-center items-center rounded-full overflow-hidden m-1 flex-shrink-0", onClick: handleAvatarClick, children: _jsx(UserAvatar, { type: "avtarStyle", candidateName: (_d = userData === null || userData === void 0 ? void 0 : userData.representative_details) === null || _d === void 0 ? void 0 : _d.full_name, src: (_e = userData === null || userData === void 0 ? void 0 : userData.representative_details) === null || _e === void 0 ? void 0 : _e.representative_image_link, width: 44, height: 44 }, (_f = userData === null || userData === void 0 ? void 0 : userData.representative_details) === null || _f === void 0 ? void 0 : _f.representative_image_link) }));
    const w = window;
    const SubscriptionRequiredModal = (_g = w.SubscriptionRequiredModal) !== null && _g !== void 0 ? _g : null;
    // Hold protected content until the access token is resolved (so the
    // refresh-token call completes before other APIs fire) behind the shared
    // app-wide loader — same visual as the route-chunk Suspense fallback, so
    // there is no white→dark handoff between "route loading" and "auth resolving".
    const gatedChildren = authReady ? children : _jsx(FullScreenLoader, {});
    return (_jsxs("div", { className: cn(`flex flex-col ${shouldIncludeAdminModule ? 'gap-4' : 'gap-0'}`, {
            'overflow-hidden h-dvh': isPreview || usesDashShell,
            'pb-10 p-3 vjwMd:p-4': shouldIncludeAdminModule,
        }), style: isPreview || usesDashShell ? { height: 'calc(var(--vh, 1vh) * 100)' } : undefined, children: [usesDashShell ? null : !isPreview && shouldIncludeAdminModule ? (_jsxs("div", { className: "flex gap-4 justify-between items-center w-full bg-white shadow-lg px-2 rounded-lg", children: [_jsx(Text, { variant: "heading", className: "py-2", children: "Admin View" }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsx(Button, { variant: "text", onClick: () => setIsManageView(true), className: `px-4 py-2 rounded-none text-sm font-medium border-b-2 ${isManageView ? 'border-b-vjwPrimary text-vjwPrimary' : 'border-transparent'} hover:border-b-vjwPrimary hover:text-vjwPrimary`, children: "AI Interview" }), _jsx(Button, { variant: "text", onClick: () => setIsManageView(false), className: `px-4 py-2 rounded-none text-sm font-medium border-b-2 ${isManageView ? 'border-transparent' : 'border-b-vjwPrimary text-vjwPrimary'} hover:border-b-vjwPrimary hover:text-vjwPrimary`, children: "Generate Script" }), avatarButton, profileMenu] })] })) : !isDashboardPage &&
                !isLandingPage &&
                // Company details is sign-up step 2: it renders the sign-up shell, not the app header.
                path !== '/company-details' &&
                isLoggedIn &&
                !accessed_from &&
                !isInterviewDetailPage ? (_jsxs("div", { className: `vdash${dashTheme === 'light' ? ' vdash-light' : ''} flex justify-end items-center w-full bg-dash-bg2 border-b border-dash-border px-4`, children: [_jsx("button", { onClick: () => navigate('/dashboard'), className: `bg-transparent text-sm font-semibold transition-colors cursor-pointer mr-4 ${path === '/dashboard'
                            ? 'text-dash-accentInk border-b-2 border-dash-accent pb-0.5'
                            : 'text-dash-text2 hover:text-dash-accentInk'}`, children: "Dashboard" }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsx("button", { onClick: () => navigate('/dashboard?view=jobs'), className: `bg-transparent text-sm font-semibold transition-colors cursor-pointer mr-4 ${path === '/dashboard' && new URLSearchParams(window.location.search).get('view') === 'jobs'
                                    ? 'text-dash-accentInk border-b-2 border-dash-accent pb-0.5'
                                    : 'text-dash-text2 hover:text-dash-accentInk'}`, children: "Home" }), avatarButton, profileMenu] })] })) : (''), isManageView ? (_jsx("div", { className: cn(usesDashShell
                    ? 'w-full h-full overflow-hidden overflow-auto'
                    : isLandingPage
                        ? 'w-full bg-containerBackground rounded-lg'
                        : 'w-full h-full bg-containerBackground overflow-y-auto rounded-lg', {
                    'shadow-lg': shouldIncludeAdminModule,
                    'shadow-slate-400': shouldIncludeAdminModule,
                    'border-gray-300': shouldIncludeAdminModule,
                    border: shouldIncludeAdminModule,
                }), children: gatedChildren })) : (_jsx("div", { className: "w-full h-full bg-containerBackground p-2 rounded-lg", children: _jsx(GenerateScriptPanelProvider, { children: _jsx(GenerateScriptPanel, { children: gatedChildren }) }) })), SubscriptionRequiredModal && _jsx(SubscriptionRequiredModal, {}), !isChildService && _jsx(ConverseChatWidget, {}), isLoggedIn && isRecruiterAppPath(path, embedHome) && _jsx(ConnectionStatusBar, {})] }));
}
export default App;
